import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import { useSelector } from 'react-redux';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const STATUS_LABELS = { '1': 'Tamamlandı', '3': 'Onay Bekliyor', '4': 'Onaylandı' };
const VAT_RATE = 0.2;

const getPriceWithoutVat = (price) => {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) return null;
  return numericPrice / (1 + VAT_RATE);
};

const formatPriceWithoutVat = (price, currency) => {
  const netPrice = getPriceWithoutVat(price);
  if (netPrice === null) return '-';
  const currencyLabel = currency === 'TRY' ? '₺' : currency || '';
  return currencyLabel ? `${netPrice.toFixed(2)} ${currencyLabel}` : netPrice.toFixed(2);
};

export default function MallManagementPetCar() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: petCarPermission, loading: permissionLoading } = useServicePermission(SERVICES.PET_CAR);

  const [startFilter, setStartFilter] = useState(moment());
  const [endFilter, setEndFilter] = useState(moment());
  const [statusFilter, setStatusFilter] = useState('');
  const [petCarList, setPetCarList] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const handleOpenImageModal = () => setImageModalOpen(true);
  const handleCloseImageModal = () => setImageModalOpen(false);
  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Pet Araba',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }
    const getLiveAnalytics = async () => {
      if (!mallID) { setLoading(false); return; }
      setLoading(true);
      try {
        const userHasPermission = petCarPermission;
        setHasPermission(userHasPermission);
        if (userHasPermission) {
          const response = await Services.GetLiveAnalyticsPetCar(parsedUser.token, mallID);
          if (response.data && response.data.result) {
            setAnalyticsData(response.data.result);
            setPetCarList(response.data.result.petCarList || []);
          } else {
            setAnalyticsData({});
            setPetCarList([]);
          }
        } else {
          setAnalyticsData({});
          setPetCarList([]);
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
        setPetCarList([]);
      } finally {
        setLoading(false);
      }
    };
    getLiveAnalytics();
  }, [mallID, parsedUser?.token, petCarPermission, permissionLoading]);

  const getLiveAnalyticsPetCarList = () => {
    if (!hasPermission) return;
    const startDate = startFilter.format('YYYY-MM-DD');
    const endDate = endFilter.format('YYYY-MM-DD');
    Services.GetLiveAnalyticsPetCarList(parsedUser.token, mallID, startDate, endDate)
      .then((res) => {
        let list = res.data.result || [];
        if (statusFilter) {
          list = list.filter(r => STATUS_LABELS[r.status] === statusFilter);
        }
        setPetCarList(list);
      })
      .catch((err) => console.error('PetCarList yüklenirken hata:', err));
  };

  const handleClearFilters = () => {
    if (!hasPermission) return;
    setStartFilter(moment());
    setEndFilter(moment());
    setStatusFilter('');
    setPetCarList(analyticsData?.petCarList || []);
  };

  useEffect(() => { setPage(1); }, [petCarList]);

  let coverImage = "/assets/mallManagementImages/Pet.png";

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const processedData = useMemo(() => {
    const data = analyticsData || {};
    const { totalAvailablePetCar = 0, totalPetCar = 0, totalDailyPetCarUsage = 0, monthlyPetCarAvarageUsage = 0, monthlyDailyHourlyUsageTotalCount = {}, weeklyDailyHourlyUsageTotalCount = {}, monthlyIncome = {}, weeklyIncome = {}, yearlyIncome = {} } = data;

    const donutSeries = totalPetCar > 0 ? [parseFloat(((totalAvailablePetCar / totalPetCar) * 100).toFixed(2)), parseFloat((((totalPetCar - totalAvailablePetCar) / totalPetCar) * 100).toFixed(2))] : [0, 0];
    const metrics = [
      { label: 'Pet Araba Adedi', primary: `Toplam: ${totalPetCar}`, secondary: `Kullanımda: ${totalPetCar - totalAvailablePetCar}`, icon: 'eva:layers-outline' },
      { label: 'Günlük Kullanım Adedi (Bugün)', primary: `Toplam: ${totalDailyPetCarUsage}`, secondary: `Ortalama: ${monthlyPetCarAvarageUsage}`, icon: 'eva:layers-outline' },
      { label: 'Doluluk Oranı', primary: `Toplam: ${totalPetCar}`, secondary: `Kullanımda: ${totalPetCar - totalAvailablePetCar}`, donut: donutSeries, colors: ['#F7A937', '#3150A0'] },
    ];

    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const DAY_MAP_TR = { monday: 'P.tesi', tuesday: 'Salı', wednesday: 'Çarş.', thursday: 'Perş.', friday: 'Cuma', saturday: 'C.tesi', sunday: 'Pazar' };
    const createHeatmap = (heatmapData) => {
      const allTimeSlots = Array.from({ length: 14 }, (_, i) => { const h = 8 + i, n = h + 1; return `${h.toString().padStart(2, '0')}:00-${n.toString().padStart(2, '0')}:00`; });

      // Her saat için satır toplamlarını hesapla
      const hourlyTotals = allTimeSlots.map(timeSlot => {
        return dayOrder.reduce((sum, dayKey) => sum + (heatmapData[dayKey]?.[timeSlot] || 0), 0);
      });

      // Her gün için sütun toplamlarını hesapla
      const dailyTotals = dayOrder.map(dayKey => {
        return allTimeSlots.reduce((sum, timeSlot) => sum + (heatmapData[dayKey]?.[timeSlot] || 0), 0);
      });

      // Genel toplam
      const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

      // En alta günlük toplamlar satırı ekle
      const totalRow = {
        name: 'Toplam',
        data: [
          ...dayOrder.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
          { x: 'Toplam', y: grandTotal }
        ]
      };

      // Series oluştur - her satıra "Toplam" sütunu ekle
      const series = [
        totalRow,
        ...allTimeSlots.map((timeSlot, index) => ({
          name: timeSlot.split('-')[0],
          data: [
            ...dayOrder.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: heatmapData[dayKey]?.[timeSlot] || 0 })),
            { x: 'Toplam', y: hourlyTotals[index] }
          ]
        })).reverse()
      ];

      const allCounts = Object.values(heatmapData).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
      const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
      const colors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
      const options = {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 2,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: colors[0] },
                { from: 1, to: Math.floor(maxCount * 0.25), color: colors[2] },
                { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: colors[3] },
                { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: colors[4] }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#000'],
            fontSize: '12px',
            fontWeight: 500
          }
        },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet` } }
      };
      return { series, options, maxCount, colors };
    };

    const monthlyHeatmap = createHeatmap(monthlyDailyHourlyUsageTotalCount || {});
    const weeklyHeatmap = createHeatmap(weeklyDailyHourlyUsageTotalCount || {});

    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthAbbrev = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(now);
    const weekRanges = Object.keys(monthlyIncome);
    const allMonthWeekKeys = ['1-7', '8-14', '15-21', '22-rest'];
    const monthlyComboCategories = allMonthWeekKeys.map((range) => `${currentMonthAbbrev} ${range.includes('rest') ? `${range.split('-')[0]}-${lastDayOfMonth}` : range}`);
    const monthlyCombo = {
      series: [
        { name: 'Ciro', type: 'column', data: allMonthWeekKeys.map(k => monthlyIncome[k]?.income || 0) },
        { name: 'Kullanım Adedi', type: 'line', data: allMonthWeekKeys.map(k => monthlyIncome[k]?.usage || 0) }
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '20%' } },
        colors: ['#1C64F2', '#16BDCA'],
        xaxis: { categories: monthlyComboCategories },
        yaxis: [
          {
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0),
            }
          },
          {
            opposite: true,
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`,
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };

    const startOfWeek = moment().startOf('isoWeek');
    const fullWeek = Array.from({ length: 7 }).map((_, i) => startOfWeek.clone().add(i, 'days'));
    const weeklyComboCategories = fullWeek.map(day => day.format('DD MMM'));
    const weeklyCombo = {
      series: [
        { name: 'Ciro', type: 'column', data: fullWeek.map(d => weeklyIncome[d.format('YYYY-MM-DD')]?.income || 0) },
        { name: 'Kullanım Adedi', type: 'line', data: fullWeek.map(d => weeklyIncome[d.format('YYYY-MM-DD')]?.usage || 0) }
      ],
      options: {
        chart: {
          type: 'line', stacked: false, toolbar: {
            show: false
          }
        },
        stroke: {
          width: [0, 3], curve: 'smooth'
        },
        markers: {
          size: 6
        },
        colors: ['#1C64F2', '#16BDCA'],
        plotOptions: { bar: { columnWidth: '50%', borderRadius: 6 } },
        xaxis: { categories: weeklyComboCategories },
        yaxis: [
          {
            title: { text: 'Kullanım Adedi' },
            labels: { formatter: (val) => val.toFixed(0) }
          },
          {
            opposite: true,
            title: { text: 'Ciro' },
            labels: { formatter: (val) => `${val.toFixed(0)} ₺` }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };
    const last13Months = Array.from({ length: 13 }).map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (12 - i));
      const year = d.getFullYear();
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      return `${year}-${monthStr}`;
    });

    const yearlyCategories = last13Months.map(monthStr => {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1, 1);
      const mFormat = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(date);
      const mTitle = mFormat.charAt(0).toUpperCase() + mFormat.slice(1);
      return `${mTitle} ${year}`;
    });

    const yearlyIncomeData = last13Months.map(m => yearlyIncome[m]?.income || 0);
    const yearlyUsageData = last13Months.map(m => yearlyIncome[m]?.usage || 0);

    const yearlyIncomeCombo = {
      series: [
        { name: 'Ciro', type: 'column', data: yearlyIncomeData },
        { name: 'Kullanım Adedi', type: 'line', data: yearlyUsageData }
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '40%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: {
          categories: yearlyCategories,
          labels: {
            rotate: -45,
            rotateAlways: false,
          }
        },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: { formatter: (val) => `${val.toFixed(0)} ₺` }
          },
          {
            opposite: true,
            title: { text: 'Adet' },
            labels: { formatter: (val) => val.toFixed(0) }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      }
    };
    // ----------------------------------------------------

    return { metrics, monthlyHeatmap, weeklyHeatmap, monthlyCombo, weeklyCombo, yearlyIncomeCombo };
  }, [analyticsData]);

  const paginatedRows = petCarList.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleExportExcel = () => {
    if (!hasPermission || petCarList.length === 0) {
      toast.warn('Export edilecek veri bulunamadı.');
      return;
    }

    const exportData = petCarList.map((r) => ({
      'NO': r.id,
      'KONUM BİLGİSİ': customer?.customer?.title || '',
      'FİYAT (KDV HARİÇ)': formatPriceWithoutVat(r.price, r.currency),
      'ARAÇ NO': r.vehicleID || '-',
      'A. ADEDİ': 1,
      'OLUŞTURMA TARİHİ': new Date(parseInt(r.createdCateTime, 10) * 1000).toLocaleString('tr-TR'),
      'DURUM': STATUS_LABELS[r.status] || r.status,
    }));

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        'NO': 'number',
        'KONUM BİLGİSİ': 'string',
        'FİYAT (KDV HARİÇ)': 'string',
        'ARAÇ NO': 'string',
        'A. ADEDİ': 'number',
        'OLUŞTURMA TARİHİ': 'string',
        'DURUM': 'string'
      },
      sheetTitle: `Pet_Araba_Listesi_${moment().format('YYYY-MM-DD')}`
    })
      .then(response => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
        } else {
          toast.error(response.data.message);
        }
      })
      .catch(error => {
        console.error('Excel export hatası:', error);
        toast.error(error.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
      });
  };

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Pet Araba</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Pet Araba</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="text" startIcon={<Iconify icon="eva:image-outline" />} sx={{ textTransform: 'none' }} onClick={handleOpenImageModal}>Tanıtım Görselleri</Button>
          <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} mb={3}>
        {processedData.metrics.map((m, i) => (
          <Grid key={i} item xs={12} md={3}>
            <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{m.label}</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>{m.primary}</Typography>
                <Typography variant="body2" color="text.secondary">{m.secondary}</Typography>
              </Box>
              {m.icon && (<Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon={m.icon} width={20} height={20} sx={{ color: '#fff' }} /></Box>)}
              {m.donut && (<Box sx={{ width: 80, height: 80 }}><ReactApexChart options={{ chart: { type: 'donut', sparkline: { enabled: true } }, plotOptions: { pie: { donut: { size: '65%', labels: { show: true, name: { show: false }, value: { show: true, fontSize: '18px', fontWeight: '700', offsetY: 0, color: '#111', formatter: (val) => `% ${val}` }, total: { show: false } } } } }, colors: m.colors, labels: ['Boşta', 'Kullanımda'], dataLabels: { enabled: false }, legend: { show: false } }} series={hasPermission ? m.donut : zeroOutSeries(m.donut)} type="donut" height={80} /></Box>)}
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} md={3}>
          <Card elevation={0} sx={{ p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Fiyat Bilgileri</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ minWidth: '60px', fontWeight: 600 }}>Günlük:</Typography>
              <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 2, py: 0.5, textAlign: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {formatPriceWithoutVat(analyticsData?.priceList?.daily?.price, analyticsData?.priceList?.daily?.currency)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: '60px', fontWeight: 600 }}>Saatlik:</Typography>
              <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 2, py: 0.5, textAlign: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {formatPriceWithoutVat(analyticsData?.priceList?.hour?.price, analyticsData?.priceList?.hour?.currency)}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
            <Box mb={1}><Typography variant="h6">Aylık Gün ve Saat Bazlı Kullanım Yoğunluğu</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Saat Aralığı</Typography></Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}><ReactApexChart options={processedData.monthlyHeatmap.options} series={hasPermission ? processedData.monthlyHeatmap.series : zeroOutSeries(processedData.monthlyHeatmap.series)} type="heatmap" height={300} /></Box>
              <HeatmapLegend maxCount={processedData.monthlyHeatmap.maxCount} colors={processedData.monthlyHeatmap.colors} label="Kullanım Adedi" />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 3, borderRadius: 2, height: 380, bgcolor: 'background.paper' }}>
            <Typography variant="h6">Haftalık Kullanım ve Ciro Hareketleri</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.monthlyCombo.options} series={hasPermission ? processedData.monthlyCombo.series : zeroOutSeries(processedData.monthlyCombo.series)} type="line" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
            <Box mb={1}><Typography variant="h6" gutterBottom>Haftalık Pet Arabası Kullanımı (Yoğun Saatler)</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Saat Aralığı</Typography></Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}><ReactApexChart options={processedData.weeklyHeatmap.options} series={hasPermission ? processedData.weeklyHeatmap.series : zeroOutSeries(processedData.weeklyHeatmap.series)} type="heatmap" height={300} /></Box>
              <HeatmapLegend maxCount={processedData.weeklyHeatmap.maxCount} colors={processedData.weeklyHeatmap.colors} label="Kullanım Adedi" />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 3, borderRadius: 2, height: 380, bgcolor: 'background.paper' }}>
            <Typography variant="h6">Günlük Kullanım ve Ciro Hareketleri</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.weeklyCombo.options} series={hasPermission ? processedData.weeklyCombo.series : zeroOutSeries(processedData.weeklyCombo.series)} type="line" height={280} />
          </Card>
        </Grid>
        {/* EKLENEN 13 AYLIK GRAFİK ALANI */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 3, borderRadius: 2, height: 380, bgcolor: 'background.paper' }}>
            <Typography variant="h6">Aylık Kullanım ve Ciro Hareketleri (Son 12 Ay)</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart 
              options={processedData.yearlyIncomeCombo.options} 
              series={hasPermission ? processedData.yearlyIncomeCombo.series : zeroOutSeries(processedData.yearlyIncomeCombo.series)} 
              type="line" 
              height={280} 
            />
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2 }} elevation={0}>
        <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startFilter} onChange={setStartFilter} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={12} sm={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endFilter} onChange={setEndFilter} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={12} sm={2}><FormControl fullWidth><InputLabel>Durum</InputLabel><Select value={statusFilter} label="Durum" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="Tamamlandı">Tamamlandı</MenuItem>
            <MenuItem value="Onaylandı">Onaylandı</MenuItem>
            <MenuItem value="Onay Bekliyor">Onay Bekliyor</MenuItem>
          </Select></FormControl></Grid>
          <Grid item xs />
          <Grid item xs={6} sm={2}><Button fullWidth variant="outlined" onClick={handleExportExcel} disabled={!hasPermission || petCarList.length === 0}>EXCEL'E AKTAR</Button></Grid>
          <Grid item xs={6} sm={1}><Button fullWidth variant="outlined" onClick={handleClearFilters} disabled={!hasPermission}>TEMİZLE</Button></Grid>
          <Grid item xs={6} sm={1}><Button fullWidth variant="contained" onClick={getLiveAnalyticsPetCarList} disabled={!hasPermission}>LİSTELE</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'background.paper' }}><TableRow><TableCell padding="checkbox"><Checkbox size="small" /></TableCell>{['NO', 'KONUM BİLGİSİ', 'FİYAT (KDV HARİÇ)', 'ARAÇ NO', 'A. ADEDİ', 'OLUŞTURMA TARİHİ', 'DURUM', 'M.NOTU'].map((h) => <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {paginatedRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell padding="checkbox"><Checkbox size="small" /></TableCell><TableCell>{r.id}</TableCell>
                  <TableCell>{customer?.customer?.title}</TableCell>
                  <TableCell>{formatPriceWithoutVat(r.price, r.currency)}</TableCell>
                  <TableCell>{r.vehicleID || "-"}</TableCell><TableCell>1</TableCell>
                  <TableCell>{new Date(parseInt(r.createdCateTime, 10) * 1000).toLocaleString('tr-TR')}</TableCell>
                  <TableCell><Chip label={STATUS_LABELS[r.status] || r.status} size="small" sx={{ bgcolor: STATUS_LABELS[r.status] === 'Tamamlandı' ? '#ECFDF3' : STATUS_LABELS[r.status] === 'Onaylandı' ? '#EDEBFE' : STATUS_LABELS[r.status] === 'Onay Bekliyor' ? '#FEF1EB' : '#FDE8E8', border: '1px solid', borderColor: STATUS_LABELS[r.status] === 'Tamamlandı' ? '#DCFAE6' : STATUS_LABELS[r.status] === 'Onaylandı' ? '#D9D6FE' : STATUS_LABELS[r.status] === 'Onay Bekliyor' ? '#FEDBD6' : '#FECDCA', color: STATUS_LABELS[r.status] === 'Tamamlandı' ? '#067647' : STATUS_LABELS[r.status] === 'Onaylandı' ? '#5925DC' : STATUS_LABELS[r.status] === 'Onay Bekliyor' ? '#DC6525' : '#B42318', borderRadius: "5px" }} /></TableCell>
                  <TableCell><Iconify icon="eva:file-text-outline" width={20} height={20} sx={{ color: theme.palette.text.secondary }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={9} sx={{ py: 2 }}><Grid container alignItems="center"><Grid item xs><Typography variant="caption">Toplam {petCarList.length} kayıt</Typography></Grid><Grid item><Pagination count={Math.ceil(petCarList.length / rowsPerPage)} page={page} onChange={(e, newPage) => setPage(newPage)} shape="rounded" size="small" /></Grid></Grid></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Tanıtım Videosu
          <IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="img" src={coverImage} alt="Tanıtım Görseli" sx={{ width: '100%', height: 'auto', borderRadius: 1 }} />
        </DialogContent>
      </Dialog>

      <Dialog open={isImageModalOpen} onClose={handleCloseImageModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Tanıtım Görselleri
          <IconButton aria-label="close" onClick={handleCloseImageModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="img" src="/assets/mallManagementImages/pet-acik.png" alt="Pet Araba Açık" sx={{ border: 'none',width: '100%', height: 'auto', borderRadius: 1 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="img" src="/assets/mallManagementImages/pet-kapali.png" alt="Pet Araba Kapalı" sx={{ border: 'none',width: '100%', height: 'auto', borderRadius: 1 }} />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
